import { useState, useEffect, useCallback } from "react";
import { useI18n } from "./i18n";
import type { HudSnapshot, GameFacade } from "./GameFacade";
import type { MapConfig } from "@bastion/data";
import { filterAvailableMaps, getAvailableMapId } from "./MapSelector";
import { formatNumber } from "./utils";

interface HudProps {
  snapshot: HudSnapshot;
  facade: GameFacade;
  maps: Record<string, MapConfig>;
  currentMapId: string;
  onMapChange?: (mapId: string) => void;
  onSettingsOpen?: () => void;
  onManagementOpen?: () => void;
  replayMode?: boolean;
}

/** 速度控制按钮的速度选项。 */
const NEXT_SPEED: Record<1 | 2 | 4, 1 | 2 | 4> = { 1: 2, 2: 4, 4: 1 };

/**
 * 顶部状态栏：金币、生命、波次，以及速度/波次与暂停控制按钮。
 * 仅在每个渲染帧的 HUD 快照变化时重新渲染。
 */
export function Hud({
  snapshot,
  facade,
  maps,
  currentMapId,
  onMapChange,
  onSettingsOpen,
  onManagementOpen,
  replayMode = false,
}: HudProps) {
  const running = snapshot.status === "running";
  const paused = snapshot.status === "paused";
  const gameOver = snapshot.status === "won" || snapshot.status === "lost";
  const { t, tEntity } = useI18n();
  const availableMaps = filterAvailableMaps(maps);
  const availableCurrentMapId = getAvailableMapId(currentMapId);

  // 自定义地图选择器状态
  const [mapSelectOpen, setMapSelectOpen] = useState(false);

  const handleMapSelectClick = useCallback(() => {
    if (!gameOver) setMapSelectOpen((v) => !v);
  }, [gameOver]);

  const handleMapChangeInner = useCallback(
    (mapId: string) => {
      setMapSelectOpen(false);
      onMapChange?.(mapId);
    },
    [onMapChange],
  );

  // 点击外部关闭下拉框
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (!target.closest(".map-select-wrapper")) {
        setMapSelectOpen(false);
      }
    }
    if (mapSelectOpen) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [mapSelectOpen]);

  return (
    <header className="hud" data-testid="hud">
      {/* FPS indicator (top-left) */}
      <div className="hud-fps" data-testid="hud-fps">
        <span className="hud-fps-label">{t("hud_fps")}</span>
        <span
          className={`hud-fps-value ${snapshot.fps >= 50 ? "ok" : snapshot.fps >= 30 ? "warn" : "bad"}`}
        >
          {snapshot.fps}
        </span>
      </div>

      {/* Wave countdown banner */}
      {snapshot.waveCountdown > 0 && (
        <div className="hud-countdown" data-testid="wave-countdown">
          <span className="countdown-label">
            {t("hud_wave_countdown", { seconds: String(Math.ceil(snapshot.waveCountdown)) })}
          </span>
        </div>
      )}

      <div className="hud-content">
        <div className="hud-stat">
          <span className="hud-label">{t("hud_gold")}</span>
          <span data-testid="hud-gold" className="hud-value gold">
            {snapshot.unlimitedGold ? "∞" : formatNumber(snapshot.gold)}
          </span>
        </div>

        <div className="hud-stat">
          <span className="hud-label">{t("hud_lives")}</span>
          <span data-testid="hud-lives" className="hud-value lives">
            {snapshot.lives}
            <span className="hud-sub">/{snapshot.maxLives}</span>
          </span>
        </div>

        <div className="hud-stat">
          <span className="hud-label">{t("hud_wave")}</span>
          <span data-testid="hud-wave" className="hud-value">
            {snapshot.wave}
            {snapshot.isEndless ? (
              <span className="hud-sub">/∞</span>
            ) : (
              <span className="hud-sub">/{snapshot.totalWaves}</span>
            )}
          </span>
        </div>
        <div className="hud-stat">
          <span className="hud-label">{t("hud_wave_enemies_count")}</span>
          <span
            className="hud-wave-enemy-count"
            data-testid="hud-wave-enemy-count"
            aria-label={t("hud_wave_enemies_count", {
              remaining: String(snapshot.waveEnemiesRemaining),
              total: String(snapshot.waveEnemiesTotal),
            })}
          >
            {snapshot.waveEnemiesRemaining}/{snapshot.waveEnemiesTotal}
          </span>
        </div>

        <div className="hud-controls">
          {!replayMode && (
            <button
              className={`hud-btn ${!gameOver && (paused || !snapshot.waveInProgress) ? "primary" : ""}`}
              data-testid="wave-control-btn"
              onClick={() => {
                if (paused) {
                  facade.resume();
                } else if (snapshot.waveInProgress) {
                  facade.pause();
                } else {
                  facade.startWave();
                }
              }}
              disabled={!running && !paused}
            >
              {paused
                ? t("hud_resume")
                : gameOver || snapshot.waveInProgress
                  ? t("hud_pause")
                  : t("hud_start_wave")}
            </button>
          )}

          {!replayMode && (
            <button
              className="hud-btn speed active"
              data-testid="speed-cycle"
              onClick={() => facade.setSpeed(NEXT_SPEED[snapshot.gameSpeed as 1 | 2 | 4])}
              disabled={snapshot.status === "won" || snapshot.status === "lost"}
              aria-label={`${t("settings_speed")}: ${snapshot.gameSpeed}×`}
            >
              {snapshot.gameSpeed}×
            </button>
          )}

          <button
            className="hud-btn"
            data-testid="settings-btn"
            onClick={onSettingsOpen}
            disabled={snapshot.status === "won" || snapshot.status === "lost"}
          >
            {t("hud_settings")}
          </button>

          <button
            className="hud-btn"
            data-testid="management-btn"
            onClick={onManagementOpen}
            disabled={snapshot.status === "won" || snapshot.status === "lost"}
          >
            {t("hud_management")}
          </button>

          {!replayMode && (
            <div className="map-select-wrapper" data-testid="hud-map-select">
              <button
                className={`map-select-btn ${mapSelectOpen ? "active" : ""}`}
                onClick={handleMapSelectClick}
                disabled={gameOver}
              >
                <span>{tEntity("map", availableCurrentMapId)}</span>
                <span className="map-select-arrow">▼</span>
              </button>
              <div className={`map-select-dropdown ${mapSelectOpen ? "open" : ""}`}>
                {Object.values(availableMaps).map((m) => (
                  <button
                    key={m.id}
                    className={`map-select-option ${m.id === availableCurrentMapId ? "selected" : ""}`}
                    onClick={() => handleMapChangeInner(m.id)}
                  >
                    {tEntity("map", m.id)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
